import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiSend,
  FiSlash,
  FiX,
} from 'react-icons/fi';
import { authAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import { formatDateTimeSeconds } from '../utils/dateUtils';

const RESULT_META = {
  SUCCESS: { colorScheme: 'green', icon: FiCheckCircle, label: '成功' },
  DUPLICATE_COOLDOWN: { colorScheme: 'orange', icon: FiClock, label: '冷卻中' },
  LEAVE_PENDING_CONFIRM: { colorScheme: 'yellow', icon: FiAlertTriangle, label: '請假待確認' },
  STATUS_ALERT: { colorScheme: 'purple', icon: FiAlertTriangle, label: '狀態提醒' },
  INVALID: { colorScheme: 'red', icon: FiSlash, label: '無效條碼' },
};

const STATUS_LABELS = {
  NOT_ARRIVED: '未到班',
  ARRIVED: '已到班',
  READY_FOR_PICKUP: '可接送',
  HOMEWORK_PENDING: '作業待處理',
  PARENT_EN_ROUTE: '家長接送途中',
  PICKUP_COMPLETED: '已接走',
  LEAVE: '請假',
};

const EVENT_TYPE_LABELS = {
  CHECK_IN: '簽到',
  NOTIFY_READY: '通知可接送',
  REPEAT_NOTIFY: '再次通知可接送',
  BLOCKED_COOLDOWN: '冷卻阻擋',
  STATUS_ALERT: '狀態提醒',
  CONFIRM_LEAVE_CHECK_IN: '請假確認簽到',
};

const RESULT_OPTIONS = [
  ['', '全部結果'],
  ['SUCCESS', '成功'],
  ['DUPLICATE_COOLDOWN', '冷卻中'],
  ['LEAVE_PENDING_CONFIRM', '請假待確認'],
  ['STATUS_ALERT', '狀態提醒'],
  ['INVALID', '無效條碼'],
];

const PAGE_SIZE = 20;

const formatStatus = (value) => STATUS_LABELS[value] || value || '--';
const formatEventType = (value) => EVENT_TYPE_LABELS[value] || value || '--';

const formatCooldown = (seconds) => {
  if (!seconds) return '0 秒';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return minutes > 0 ? `${minutes} 分 ${remainingSeconds} 秒` : `${seconds} 秒`;
};

const ResultBadge = ({ result }) => {
  const meta = RESULT_META[result] || { colorScheme: 'gray', icon: FiAlertTriangle, label: result };

  return (
    <Badge colorScheme={meta.colorScheme} px={3} py={1} borderRadius="full">
      <HStack spacing={1}>
        <Icon as={meta.icon} />
        <Text>{meta.label}</Text>
      </HStack>
    </Badge>
  );
};

const EventCard = ({ item }) => {
  const studentName = item.student?.full_name || `學生 #${item.student_id}`;
  const className = item.student?.class_name || item.student?.class?.name || '--';

  return (
    <Card borderWidth="1px" borderColor="gray.200" shadow="sm">
      <CardBody>
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap={3}
          direction={{ base: 'column', md: 'row' }}
        >
          <VStack align="stretch" spacing={1} flex="1">
            <HStack wrap="wrap" spacing={2}>
              <Heading size="sm">{studentName}</Heading>
              <ResultBadge result={item.result} />
              <Badge variant="outline" colorScheme="gray">
                {formatEventType(item.event_type)}
              </Badge>
            </HStack>
            <Text color="gray.600" fontSize="sm">班級：{className}</Text>
            <Text color="gray.600" fontSize="sm">
              學生狀態：{formatStatus(item.status_before)} {'->'} {formatStatus(item.status_after)}
            </Text>
            {item.note ? <Text color="gray.500" fontSize="sm">{item.note}</Text> : null}
          </VStack>

          <VStack align={{ base: 'flex-start', md: 'flex-end' }} spacing={1} minW={{ md: '170px' }}>
            <Text fontWeight="bold" color="gray.700">{formatDateTimeSeconds(item.event_time)}</Text>
            <Text color="gray.500" fontSize="sm">
              {RESULT_META[item.result]?.label || item.result}
            </Text>
          </VStack>
        </Flex>
      </CardBody>
    </Card>
  );
};

const FrontdeskPage = () => {
  const toast = useToast();
  const scannerInputRef = useRef(null);
  const confirmModal = useDisclosure();
  const { lastDataUpdate } = useNotifications();

  const [tabIndex, setTabIndex] = useState(0);
  const [classes, setClasses] = useState([]);
  const [scanCode, setScanCode] = useState('');
  const [leaveConfirmNote, setLeaveConfirmNote] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [queryEvents, setQueryEvents] = useState([]);
  const [queryForm, setQueryForm] = useState({ query_date: '', class_id: '', student_name: '', result: '' });
  const [queryPage, setQueryPage] = useState(1);
  const [queryTotalCount, setQueryTotalCount] = useState(0);
  const [pendingLeaveStudent, setPendingLeaveStudent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshingRecent, setIsRefreshingRecent] = useState(false);
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [isConfirmingLeave, setIsConfirmingLeave] = useState(false);
  const [hasLoadedQuery, setHasLoadedQuery] = useState(false);

  const totalQueryPages = Math.max(1, Math.ceil(queryTotalCount / PAGE_SIZE));

  const focusScanner = () => {
    window.setTimeout(() => {
      scannerInputRef.current?.focus();
      scannerInputRef.current?.select();
    }, 0);
  };

  const notifyError = (title, error) => {
    toast({
      title,
      description: error.response?.data?.detail || '請稍後再試。',
      status: 'error',
      duration: 4000,
      isClosable: true,
    });
  };

  const fetchRecentEvents = async (silent = false) => {
    if (!silent) setIsRefreshingRecent(true);

    try {
      const response = await authAPI.frontdesk.getRecentEvents(12);
      setRecentEvents(response.data?.items || []);
    } catch (error) {
      notifyError('最近紀錄讀取失敗', error);
    } finally {
      if (!silent) setIsRefreshingRecent(false);
    }
  };

  const fetchQueryEvents = async (page = 1, form = queryForm) => {
    setIsQueryLoading(true);

    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        ...(form.query_date && { query_date: form.query_date }),
        ...(form.class_id && { class_id: Number(form.class_id) }),
        ...(form.student_name.trim() && { student_name: form.student_name.trim() }),
        ...(form.result && { result: form.result }),
      };

      const response = await authAPI.frontdesk.getEvents(params);
      setQueryEvents(response.data || []);
      setQueryPage(page);
      setQueryTotalCount(parseInt(response.headers?.['x-total-count'] || '0', 10));
      setHasLoadedQuery(true);
    } catch (error) {
      notifyError('查詢紀錄失敗', error);
    } finally {
      setIsQueryLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentEvents();
    focusScanner();
    authAPI
      .getClassesLite()
      .then((response) => setClasses(response.data || []))
      .catch((error) => notifyError('班級資料讀取失敗', error));
  }, []);

  useEffect(() => {
    fetchRecentEvents(true);
    if (hasLoadedQuery) fetchQueryEvents(queryPage);
  }, [lastDataUpdate]);

  useEffect(() => {
    if (tabIndex === 0) focusScanner();
    if (tabIndex === 1 && !hasLoadedQuery) fetchQueryEvents(1);
  }, [tabIndex]);

  const handleScanSubmit = async (event) => {
    event.preventDefault();
    if (!scanCode.trim()) return focusScanner();

    setIsSubmitting(true);

    try {
      const response = await authAPI.frontdesk.scan(scanCode.trim());
      setLastResult(response.data);
      setScanCode('');
      await fetchRecentEvents(true);
      if (hasLoadedQuery) await fetchQueryEvents(1);

      if (response.data.result === 'LEAVE_PENDING_CONFIRM' && response.data.student) {
        setPendingLeaveStudent(response.data.student);
        setLeaveConfirmNote('');
        confirmModal.onOpen();
      }
    } catch (error) {
      notifyError('送出簽到失敗', error);
    } finally {
      setIsSubmitting(false);
      focusScanner();
    }
  };

  const handleConfirmLeave = async () => {
    if (!pendingLeaveStudent?.id) return;

    setIsConfirmingLeave(true);

    try {
      const response = await authAPI.frontdesk.confirmLeaveCheckin(
        pendingLeaveStudent.id,
        leaveConfirmNote.trim() || null,
      );
      setLastResult(response.data);
      setPendingLeaveStudent(null);
      setLeaveConfirmNote('');
      confirmModal.onClose();
      await fetchRecentEvents(true);
      if (hasLoadedQuery) await fetchQueryEvents(1);
      toast({
        title: '已完成請假簽到確認',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      notifyError('請假確認失敗', error);
    } finally {
      setIsConfirmingLeave(false);
      focusScanner();
    }
  };

  const latestEventType = formatEventType(lastResult?.event?.event_type);
  const lastStudentClass = lastResult?.student?.class_name || lastResult?.student?.class?.name || '--';

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', lg: 'center' }}
        direction={{ base: 'column', lg: 'row' }}
        gap={4}
        mb={6}
      >
        <Box>
          <Heading size="lg" mb={2}>簽到系統</Heading>
          <Text color="gray.600">掃碼與查詢分頁獨立，前台可快速簽到，行政可回查完整事件紀錄。</Text>
        </Box>
        <HStack>
          <Button
            leftIcon={<FiRefreshCw />}
            variant="outline"
            onClick={() => {
              fetchRecentEvents();
              if (hasLoadedQuery) fetchQueryEvents(queryPage);
            }}
            isLoading={isRefreshingRecent || isQueryLoading}
          >
            重新整理
          </Button>
          <Button leftIcon={<FiCheckCircle />} colorScheme="brand" onClick={focusScanner}>
            聚焦輸入框
          </Button>
        </HStack>
      </Flex>

      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="brand" variant="soft-rounded" isLazy>
        <TabList mb={6}>
          <Tab>掃碼頁</Tab>
          <Tab>查詢頁</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={6}>
              <Card shadow="sm" borderWidth="1px">
                <CardHeader pb={2}>
                  <Heading size="md">簽到輸入</Heading>
                </CardHeader>
                <CardBody>
                  <form onSubmit={handleScanSubmit}>
                    <VStack align="stretch" spacing={4}>
                      <Alert status="info" borderRadius="lg">
                        <AlertIcon />
                        <Box>
                          <AlertTitle fontSize="sm">操作提示</AlertTitle>
                          <AlertDescription fontSize="sm">
                            請先把游標停在輸入框，掃碼槍完成後會自動送出。
                          </AlertDescription>
                        </Box>
                      </Alert>

                      <FormControl>
                        <FormLabel>磁扣 / 卡號</FormLabel>
                        <InputGroup size="lg">
                          <Input
                            ref={scannerInputRef}
                            value={scanCode}
                            onChange={(event) => setScanCode(event.target.value)}
                            placeholder="刷磁扣或輸入卡號後按 Enter"
                            autoComplete="off"
                            spellCheck={false}
                            bg="white"
                          />
                          {scanCode ? (
                            <InputRightElement h="full" pr={2}>
                              <IconButton
                                aria-label="清空條碼"
                                icon={<FiX />}
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setScanCode('');
                                  focusScanner();
                                }}
                              />
                            </InputRightElement>
                          ) : null}
                        </InputGroup>
                        <FormHelperText>冷卻中時請依剩餘秒數稍後再掃。</FormHelperText>
                      </FormControl>

                      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
                        <Card bg="green.50" borderWidth="1px">
                          <CardBody>
                            <Text fontWeight="bold" color="green.700">第一次掃碼</Text>
                            <Text fontSize="sm" color="green.600">學生狀態：已到班</Text>
                          </CardBody>
                        </Card>
                        <Card bg="blue.50" borderWidth="1px">
                          <CardBody>
                            <Text fontWeight="bold" color="blue.700">第二次掃碼</Text>
                            <Text fontSize="sm" color="blue.600">學生狀態：可接送</Text>
                          </CardBody>
                        </Card>
                        <Card bg="orange.50" borderWidth="1px">
                          <CardBody>
                            <Text fontWeight="bold" color="orange.700">例外處理</Text>
                            <Text fontSize="sm" color="orange.600">請假確認與狀態提醒都保留紀錄。</Text>
                          </CardBody>
                        </Card>
                      </Grid>

                      <HStack justify="space-between">
                        <Button
                          leftIcon={<FiRotateCcw />}
                          variant="ghost"
                          onClick={() => {
                            setScanCode('');
                            focusScanner();
                          }}
                        >
                          清空
                        </Button>
                        <Button type="submit" colorScheme="brand" leftIcon={<FiSend />} isLoading={isSubmitting}>
                          送出簽到
                        </Button>
                      </HStack>
                    </VStack>
                  </form>
                </CardBody>
              </Card>

              <Card shadow="sm" borderWidth="1px">
                <CardHeader pb={2}>
                  <Heading size="md">最新結果</Heading>
                </CardHeader>
                <CardBody>
                  {!lastResult ? (
                    <Flex minH="320px" align="center" justify="center" direction="column" color="gray.500">
                      <Icon as={FiCheckCircle} boxSize={10} mb={3} color="gray.300" />
                      <Text>尚未進行任何簽到操作。</Text>
                    </Flex>
                  ) : (
                    <VStack align="stretch" spacing={4}>
                      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                        <ResultBadge result={lastResult.result} />
                        {lastResult.cooldown_remaining_seconds > 0 ? (
                          <Badge colorScheme="orange" px={3} py={1} borderRadius="full">
                            冷卻剩餘：{formatCooldown(lastResult.cooldown_remaining_seconds)}
                          </Badge>
                        ) : null}
                      </Flex>

                      <Alert
                        status={
                          RESULT_META[lastResult.result]?.colorScheme === 'red'
                            ? 'error'
                            : ['yellow', 'purple'].includes(RESULT_META[lastResult.result]?.colorScheme)
                              ? 'warning'
                              : 'success'
                        }
                        borderRadius="lg"
                      >
                        <AlertIcon />
                        <Box>
                          <AlertTitle>{lastResult.message}</AlertTitle>
                          <AlertDescription>
                            {lastResult.event?.event_type
                              ? `事件類型：${latestEventType}`
                              : '目前沒有額外事件紀錄。'}
                          </AlertDescription>
                        </Box>
                      </Alert>

                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Stat bg="gray.50" borderRadius="lg" p={4}>
                          <StatLabel>學生</StatLabel>
                          <StatNumber fontSize="2xl">{lastResult.student?.full_name || '--'}</StatNumber>
                          <StatHelpText>班級：{lastStudentClass}</StatHelpText>
                        </Stat>
                        <Stat bg="gray.50" borderRadius="lg" p={4}>
                          <StatLabel>學生狀態</StatLabel>
                          <StatNumber fontSize="2xl">
                            {formatStatus(lastResult.event?.status_before)} {'->'} {formatStatus(lastResult.event?.status_after)}
                          </StatNumber>
                          <StatHelpText>結果：{RESULT_META[lastResult.result]?.label || lastResult.result}</StatHelpText>
                        </Stat>
                      </SimpleGrid>

                      {lastResult.result === 'LEAVE_PENDING_CONFIRM' && pendingLeaveStudent ? (
                        <Alert status="warning" borderRadius="lg">
                          <AlertIcon />
                          <Box flex="1">
                            <AlertTitle>這位學生目前是請假狀態</AlertTitle>
                            <AlertDescription>如果確認已到班，請點下方按鈕完成請假簽到確認。</AlertDescription>
                          </Box>
                          <Button colorScheme="yellow" onClick={confirmModal.onOpen}>
                            開啟確認
                          </Button>
                        </Alert>
                      ) : null}
                    </VStack>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>

            <Card shadow="sm" borderWidth="1px">
              <CardHeader pb={2}>
                <Flex
                  justify="space-between"
                  align={{ base: 'flex-start', md: 'center' }}
                  direction={{ base: 'column', md: 'row' }}
                  gap={3}
                >
                  <Box>
                    <Heading size="md">最近紀錄</Heading>
                    <Text color="gray.600" fontSize="sm">固定顯示最新 12 筆。</Text>
                  </Box>
                  <Badge colorScheme="gray" px={3} py={1} borderRadius="full">
                    共 {recentEvents.length} 筆
                  </Badge>
                </Flex>
              </CardHeader>
              <CardBody>
                {isRefreshingRecent && recentEvents.length === 0 ? (
                  <Flex justify="center" py={10}>
                    <Spinner />
                  </Flex>
                ) : recentEvents.length === 0 ? (
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>目前還沒有最近紀錄。</AlertDescription>
                  </Alert>
                ) : (
                  <Stack spacing={3}>
                    {recentEvents.map((item, index) => (
                      <Box key={item.id}>
                        <EventCard item={item} />
                        {index < recentEvents.length - 1 ? <Divider /> : null}
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6}>
              <Card shadow="sm" borderWidth="1px">
                <CardHeader pb={2}>
                  <Heading size="md">查詢條件</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <FormControl>
                      <FormLabel>日期</FormLabel>
                      <Input
                        type="date"
                        value={queryForm.query_date}
                        onChange={(event) => setQueryForm((prev) => ({ ...prev, query_date: event.target.value }))}
                        bg="white"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>班級</FormLabel>
                      <Select
                        placeholder="全部班級"
                        value={queryForm.class_id}
                        onChange={(event) => setQueryForm((prev) => ({ ...prev, class_id: event.target.value }))}
                        bg="white"
                      >
                        {classes.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>學生姓名</FormLabel>
                      <Input
                        value={queryForm.student_name}
                        onChange={(event) => setQueryForm((prev) => ({ ...prev, student_name: event.target.value }))}
                        placeholder="可輸入關鍵字"
                        bg="white"
                      />
                      <FormHelperText>支援部分姓名查詢。</FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>結果類型</FormLabel>
                      <Select
                        value={queryForm.result}
                        onChange={(event) => setQueryForm((prev) => ({ ...prev, result: event.target.value }))}
                        bg="white"
                      >
                        {RESULT_OPTIONS.map(([value, label]) => (
                          <option key={value || 'all'} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <HStack>
                      <Button
                        leftIcon={<FiSearch />}
                        colorScheme="brand"
                        flex="1"
                        onClick={() => fetchQueryEvents(1)}
                        isLoading={isQueryLoading}
                      >
                        開始查詢
                      </Button>
                      <Button
                        leftIcon={<FiRotateCcw />}
                        variant="outline"
                        onClick={() => {
                          const reset = { query_date: '', class_id: '', student_name: '', result: '' };
                          setQueryForm(reset);
                          fetchQueryEvents(1, reset);
                        }}
                        isDisabled={isQueryLoading}
                      >
                        重設
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              <Card shadow="sm" borderWidth="1px" gridColumn={{ xl: 'span 2' }}>
                <CardHeader pb={2}>
                  <Flex
                    justify="space-between"
                    align={{ base: 'flex-start', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap={3}
                  >
                    <Box>
                      <Heading size="md">查詢結果</Heading>
                      <Text color="gray.600" fontSize="sm">
                        共 {queryTotalCount} 筆，現在第 {queryPage} / {totalQueryPages} 頁
                      </Text>
                    </Box>
                    <HStack>
                      <Button
                        size="sm"
                        leftIcon={<FiChevronLeft />}
                        variant="outline"
                        onClick={() => fetchQueryEvents(queryPage - 1)}
                        isDisabled={queryPage <= 1 || isQueryLoading}
                      >
                        上一頁
                      </Button>
                      <Button
                        size="sm"
                        rightIcon={<FiChevronRight />}
                        variant="outline"
                        onClick={() => fetchQueryEvents(queryPage + 1)}
                        isDisabled={queryPage >= totalQueryPages || isQueryLoading}
                      >
                        下一頁
                      </Button>
                    </HStack>
                  </Flex>
                </CardHeader>
                <CardBody>
                  {isQueryLoading ? (
                    <Flex justify="center" py={10}>
                      <Spinner />
                    </Flex>
                  ) : !hasLoadedQuery ? (
                    <Alert status="info" borderRadius="lg">
                      <AlertIcon />
                      <AlertDescription>請先查詢，或直接查看全部紀錄。</AlertDescription>
                    </Alert>
                  ) : queryEvents.length === 0 ? (
                    <Alert status="info" borderRadius="lg">
                      <AlertIcon />
                      <AlertDescription>目前查無符合條件的紀錄。</AlertDescription>
                    </Alert>
                  ) : (
                    <Stack spacing={3}>
                      {queryEvents.map((item, index) => (
                        <Box key={item.id}>
                          <EventCard item={item} />
                          {index < queryEvents.length - 1 ? <Divider /> : null}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={confirmModal.isOpen} onClose={confirmModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>確認請假學生簽到</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text>
                是否確認 <Text as="span" fontWeight="bold">{pendingLeaveStudent?.full_name || '--'}</Text> 已到班並完成簽到？
              </Text>
              <FormControl>
                <FormLabel>備註</FormLabel>
                <Input
                  value={leaveConfirmNote}
                  onChange={(event) => setLeaveConfirmNote(event.target.value)}
                  placeholder="若需要可補充原因或備註"
                />
                <FormHelperText>這筆備註會保留在簽到系統的事件紀錄中。</FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={confirmModal.onClose}>
              取消
            </Button>
            <Button colorScheme="yellow" onClick={handleConfirmLeave} isLoading={isConfirmingLeave}>
              確認簽到
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FrontdeskPage;
